import { useState } from 'react';
import {
  Modal,
  ModalContent,
  Button,
  Input,
  Control,
  Label,
  PlusIcon,
} from '@forge/common';
import { useApiMutation } from '@/hooks/useApiMutation';
import { clientsApi } from '@/api/clients';
import type { CreateClientPayload } from '@/api/clients';

interface AddClientDialogProps {
  partnerId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const AddClientDialog = ({ partnerId, onClose, onSuccess }: AddClientDialogProps) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [notes, setNotes] = useState('');

  const { mutate: createClient, isSubmitting } = useApiMutation<CreateClientPayload, void>({
    mutationFn: async (data) => {
      await clientsApi.create(data);
    },
    successMessage: 'Client added successfully',
    errorMessage: 'Failed to add client',
    onSuccess: () => onSuccess(),
  });

  const isValid = name.trim() && email.trim();

  const handleSubmit = async () => {
    if (!isValid) return;
    await createClient({
      partnerId,
      name: name.trim(),
      email: email.trim(),
      company: company.trim() || undefined,
      notes: notes.trim() || undefined,
    });
  };

  return (
    <Modal
      title="Add Client"
      onModalClose={onClose}
      onBackdropClick={onClose}
      footerLeft={
        <Button variant="tertiary" onClick={onClose}>
          Cancel
        </Button>
      }
      footerRight={
        <Button
          variant="primary"
          icon={<PlusIcon />}
          onClick={handleSubmit}
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting ? 'Adding...' : 'Add Client'}
        </Button>
      }
    >
      <ModalContent padded>
        <div className="flex flex-col gap-3">
          <Control hideSpacing="bottom">
            <Label htmlFor="client-name">Name</Label>
            <Input
              id="client-name"
              placeholder="e.g., Rebecca Blevins"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Control>

          <Control hideSpacing="bottom">
            <Label htmlFor="client-email">Email</Label>
            <Input
              id="client-email"
              type="email"
              placeholder="e.g., rebecca.blevins@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </Control>

          <Control hideSpacing="bottom">
            <Label htmlFor="client-company">Company (Optional)</Label>
            <Input
              id="client-company"
              placeholder="e.g., JCPL"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            />
          </Control>

          <Control hideSpacing="bottom">
            <Label htmlFor="client-notes">Notes (Optional)</Label>
            <Input
              id="client-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Control>

          <p className="text-xs text-color-tertiary">
            A client is an external person at this partner utility. They do not get a
            portal login — provision SFTP access separately to let them post or collect
            files over SSH.
          </p>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default AddClientDialog;
